import React, { useEffect, useState } from 'react'
import { X, Plus, Trash2, Edit3, Save } from 'lucide-react'
import { quickReplyService } from '../../services/quickReplyService'
import type { QuickReply } from '../../types/quickReply.types'
import { ConfirmDeleteModal } from '../common/ConfirmDeleteModal'
import toast from 'react-hot-toast'

interface QuickRepliesManagerModalProps {
  isOpen: boolean
  onClose: () => void
}

export const QuickRepliesManagerModal: React.FC<QuickRepliesManagerModalProps> = ({
  isOpen,
  onClose
}) => {
  const [quickReplies, setQuickReplies] = useState<QuickReply[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [editingId, setEditingId] = useState<number | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<QuickReply | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const loadQuickReplies = async () => {
    try {
      setIsLoading(true)
      const res = await quickReplyService.getQuickReplies()
      if (res.success && Array.isArray(res.data)) {
        setQuickReplies(res.data)
      } else {
        setQuickReplies([])
      }
    } catch {
      toast.error('Erro ao carregar mensagens rápidas')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (!isOpen) return
    loadQuickReplies()
  }, [isOpen])

  const resetForm = () => {
    setTitle('')
    setContent('')
    setEditingId(null)
  }

  const handleCreateOrUpdate = async () => {
    if (!title.trim() || !content.trim()) {
      toast.error('Título e conteúdo são obrigatórios')
      return
    }

    try {
      setIsSaving(true)
      if (editingId == null) {
        const res = await quickReplyService.createQuickReply({ title: title.trim(), content: content.trim() })
        if (res.success && res.data) {
          setQuickReplies(prev => [res.data as QuickReply, ...prev])
          resetForm()
          toast.success('Mensagem rápida criada')
        } else {
          toast.error(res.message || 'Erro ao criar mensagem rápida')
        }
      } else {
        const res = await quickReplyService.updateQuickReply(editingId, { title: title.trim(), content: content.trim() })
        if (res.success && res.data) {
          setQuickReplies(prev => prev.map(qr => (qr.id === editingId ? (res.data as QuickReply) : qr)))
          resetForm()
          toast.success('Mensagem rápida atualizada')
        } else {
          toast.error(res.message || 'Erro ao atualizar mensagem rápida')
        }
      }
    } catch {
      toast.error('Erro ao salvar mensagem rápida')
    } finally {
      setIsSaving(false)
    }
  }

  const handleEdit = (qr: QuickReply) => {
    setEditingId(qr.id)
    setTitle(qr.title)
    setContent(qr.content)
  }

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return
    try {
      setIsDeleting(true)
      const res = await quickReplyService.deleteQuickReply(deleteTarget.id)
      if (res.success) {
        setQuickReplies(prev => prev.filter(qr => qr.id !== deleteTarget.id))
        if (editingId === deleteTarget.id) {
          resetForm()
        }
        setDeleteTarget(null)
        toast.success('Mensagem rápida excluída')
      } else {
        toast.error(res.message || 'Erro ao excluir mensagem rápida')
      }
    } catch {
      toast.error('Erro ao excluir mensagem rápida')
    } finally {
      setIsDeleting(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h2 className="text-sm font-semibold text-gray-900">Gerenciar mensagens rápidas</h2>
          <button
            onClick={() => {
              resetForm()
              onClose()
            }}
            className="p-1 rounded hover:bg-gray-100"
          >
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        <div className="p-4 border-b space-y-2">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-medium text-gray-700">
              {editingId ? 'Editar mensagem rápida' : 'Nova mensagem rápida'}
            </span>
            {editingId && (
              <button
                type="button"
                className="flex items-center text-xs text-gray-500 hover:text-gray-700"
                onClick={resetForm}
              >
                <Plus className="w-3 h-3 mr-1" />
                Nova
              </button>
            )}
          </div>
          <input
            type="text"
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="Título (ex: Saudação padrão)"
            className="w-full px-2 py-1 border rounded text-sm"
          />
          <textarea
            value={content}
            onChange={e => setContent(e.target.value)}
            placeholder="Conteúdo da mensagem rápida..."
            rows={3}
            className="w-full px-2 py-1 border rounded text-sm resize-y"
          />
          <button
            type="button"
            onClick={handleCreateOrUpdate}
            disabled={isSaving}
            className="inline-flex items-center px-3 py-1.5 bg-blue-500 text-white text-xs rounded hover:bg-blue-600 disabled:opacity-50"
          >
            {isSaving ? (
              'Salvando...'
            ) : (
              <>
                <Save className="w-3 h-3 mr-1" />
                {editingId ? 'Atualizar' : 'Salvar'}
              </>
            )}
          </button>
        </div>

        <div className="p-3 flex-1 overflow-y-auto space-y-2 text-sm">
          {isLoading ? (
            <p className="text-gray-500 text-center py-4">Carregando mensagens...</p>
          ) : quickReplies.length === 0 ? (
            <p className="text-gray-500 text-center py-4">
              Nenhuma mensagem rápida cadastrada ainda.
            </p>
          ) : (
            quickReplies.map(qr => (
              <div
                key={qr.id}
                className="border rounded p-2 flex items-start justify-between space-x-2"
              >
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 text-xs truncate">{qr.title}</p>
                  <p className="text-xs text-gray-600 mt-1 whitespace-pre-wrap break-words">
                    {qr.content}
                  </p>
                </div>
                <div className="flex flex-col items-end space-y-1 ml-2">
                  <button
                    type="button"
                    className="p-1 rounded hover:bg-gray-100"
                    onClick={() => handleEdit(qr)}
                    title="Editar"
                  >
                    <Edit3 className="w-3 h-3 text-gray-600" />
                  </button>
                  <button
                    type="button"
                    className="p-1 rounded hover:bg-red-50"
                    onClick={() => setDeleteTarget(qr)}
                    title="Excluir"
                  >
                    <Trash2 className="w-3 h-3 text-red-500" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <ConfirmDeleteModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
        title="Excluir mensagem rápida"
        message={
          deleteTarget ? (
            <>
              Deseja realmente excluir <strong>"{deleteTarget.title}"</strong>?
              <span className="block mt-1 text-gray-500">Esta ação não pode ser desfeita.</span>
            </>
          ) : (
            ''
          )
        }
        confirmLabel="Excluir"
        isLoading={isDeleting}
      />
    </div>
  )
}

